import { hasScope } from '@nyce/admin-kit/api';
import { authClientFor } from '../../identity/session';
import { withClaims } from '../../identity/claimCatalog';
import type { AliasMatch, ServiceClient, TenantClaim } from '../../identity/api';

/** AUTH steps the wizards share, over the `/auth-admin` proxy of one environment. */

const notFound = (e: unknown) => (e as { status?: number }).status === 404;

/** Who holds the alias `system:value` (404 = nobody). */
export async function aliasOwner(target: string, system: string, value: string): Promise<AliasMatch | null> {
  try {
    return await authClientFor(target).lookupAlias(system, value);
  } catch (e) {
    if (notFound(e)) return null;
    throw e;
  }
}

/** Add `system:value` to a tenant's (or customer's) aliases, keeping the others. */
export async function addAlias(
  target: string,
  owner: 'tenants' | 'customers',
  id: string,
  system: string,
  value: string
): Promise<void> {
  const c = authClientFor(target);
  const api = owner === 'tenants' ? c.tenants.aliases : c.customers.aliases;
  const list = await api.list(id);
  if (list.some((a) => a.system === system && a.value.toLowerCase() === value.toLowerCase())) return;
  await api.replace(id, [...list.map(({ system, value }) => ({ system, value })), { system, value }]);
}

/** The scopes of a tenant's claims. */
export const scopesOf = (claims: TenantClaim[] | null | undefined) =>
  (claims ?? []).filter((c) => c.type === 'scope').map((c) => c.value);

/** Which of `wanted` the tenant's claims do not cover (wildcards count). */
export const missingScopes = (claims: TenantClaim[] | null | undefined, wanted: string[]) =>
  wanted.filter((w) => !hasScope(scopesOf(claims), w));

/** Add scope claims to a tenant, keeping the others. */
export async function addScopes(target: string, tenantId: string, scopes: string[]): Promise<void> {
  const c = authClientFor(target);
  const claims = await c.tenants.claims.list(tenantId);
  const next = withClaims(
    claims.map(({ type, value }) => ({ type, value })),
    scopes.map((value) => ({ type: 'scope', value }))
  );
  if (next.length !== claims.length) await c.tenants.claims.replace(tenantId, next);
}

/** An active client of the tenant that holds every one of `scopes`. */
export const clientWith = (clients: ServiceClient[] | null | undefined, scopes: string[]) =>
  (clients ?? []).find((c) => c.status === 'Active' && scopes.every((s) => hasScope(c.scopes, s)));
